import React from 'react';
import { FaSpinner } from 'react-icons/fa';

const AgreementModal = ({
  isOpen,
  onClose,
  application,
  approvalData,
  setApprovalData,
  loadingApprovalMeta,
  updatingStatus,
  onSubmit,
  mode = 'approve' // 'approve' or 'edit'
}) => {
  if (!isOpen || !application) return null;

  const isEditMode = mode === 'edit';
  const modalTitle = isEditMode ? 'Edit Payout Details' : 'Approve Application & Set Agreement';
  const modalSubtitle = isEditMode 
    ? `Update commission details for ${application.name}`
    : `Configure commission details for ${application.name}`;
  const submitButtonText = isEditMode ? 'Save Changes' : 'Approve & Save';
  const submitButtonLoadingText = isEditMode ? 'Saving...' : 'Approving...';

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl max-w-2xl w-full max-h-[70vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="lg:text-xl text-base font-bold text-gray-900">
            {modalTitle}
          </h2>
          <p className="lg:text-sm text-xs text-gray-600 mt-1">
            {modalSubtitle}
          </p>
        </div>

        <div className="p-6 space-y-4">
          {/* Currency (read-only) */}
          <div>
            <label className="block lg:text-sm text-xs font-medium text-gray-700 mb-1">
              Currency
            </label>
            <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 lg:text-sm text-xs text-gray-800">
              {loadingApprovalMeta && !approvalData.currency
                ? 'Loading...'
                : approvalData.currency
                  ? `${approvalData.currency.code} (${approvalData.currency.symbol})`
                  : 'Not available'}
            </div>
          </div>

          {/* Commission Model */}
          <div>
            <label className="block lg:text-sm text-xs font-medium text-gray-700 mb-1">
              Commission Model <span className="text-red-500">*</span>
            </label>
            <select
              value={approvalData.commissionModel}
              onChange={(e) =>
                setApprovalData((prev) => ({
                  ...prev,
                  commissionModel: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 lg:text-sm text-xs"
            >
              <option value="fixed">Fixed</option>
              <option value="percentage">Percentage</option>
            </select>
          </div>

          {/* Fixed Amount */}
          {approvalData.commissionModel === 'fixed' && (
            <div>
              <label className="block lg:text-sm text-xs font-medium text-gray-700 mb-1">
                Fixed Amount <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={approvalData.amount}
                onChange={(e) =>
                  setApprovalData((prev) => ({
                    ...prev,
                    amount: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 lg:text-sm text-xs"
                placeholder="Enter fixed commission amount (0 is allowed)"
              />
            </div>
          )}

          {/* Percentage */}
          {approvalData.commissionModel === 'percentage' && (
            <div>
              <label className="block lg:text-sm text-xs font-medium text-gray-700 mb-1">
                Percentage <span className="text-red-500">*</span>
              </label>
              <input
              onWheel={(e) => e.target.blur()}
                type="number"
                min="0"
                max="100"
                step="0.01"
                value={approvalData.percentage}
                onChange={(e) =>
                  setApprovalData((prev) => ({
                    ...prev,
                    percentage: e.target.value,
                  }))
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 lg:text-sm text-xs"
                placeholder="Enter percentage (0 is allowed)"
              />
            </div>
          )}

          {/* CPE */}
          <div>
            <label className="block lg:text-sm text-xs font-medium text-gray-700 mb-1">
              CPE (Cost Per Engagement) <span className="text-red-500">*</span>
            </label>
            <input
            onWheel={(e) => e.target.blur()}
              type="number"
              min="0"
              step="0.01"
              value={approvalData.cpe}
              onChange={(e) =>
                setApprovalData((prev) => ({
                  ...prev,
                  cpe: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 lg:text-sm text-xs"
              placeholder="Enter CPE (0 is allowed)"
            />
            <p className="lg:text-xs text-xs text-gray-500 mt-1">
              Cost per engagement for advertising metrics
            </p>
          </div>

          {/* CPM */}
          <div>
            <label className="block lg:text-sm text-xs font-medium text-gray-700 mb-1">
              CPM (Cost Per Thousand Views) <span className="text-red-500">*</span>
            </label>
            <input
            onWheel={(e) => e.target.blur()}
              type="number"
              min="0"
              step="0.01"
              value={approvalData.cpm}
              onChange={(e) =>
                setApprovalData((prev) => ({
                  ...prev,
                  cpm: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 lg:text-sm text-xs"
              placeholder="Enter CPM (0 is allowed)"
            />
            <p className="lg:text-xs text-xs text-gray-500 mt-1">
              Cost per thousand views for advertising metrics
            </p>
          </div>

          {/* Agreement Status */}
          <div>
            <label className="block lg:text-sm text-xs font-medium text-gray-700 mb-1">
              Agreement Status <span className="text-red-500">*</span>
            </label>
            <select
              value={approvalData.agreementStatus}
              onChange={(e) =>
                setApprovalData((prev) => ({
                  ...prev,
                  agreementStatus: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 lg:text-sm text-xs"
            >
              <option value="agreed_with_influencer">Agreed with influencer</option>
              <option value="not_agreed_with_influencer">Not agreed with influencer</option>
            </select>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={onSubmit}
              disabled={updatingStatus[application._id]}
              className={`flex-1 px-4 py-2 rounded-lg transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed ${
                isEditMode
                  ? 'bg-orange-500 text-white hover:bg-orange-600'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {updatingStatus[application._id] ? (
                <span className="flex items-center justify-center">
                  <FaSpinner className="animate-spin mr-2" />
                  {submitButtonLoadingText}
                </span>
              ) : (
                submitButtonText
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgreementModal;